import { startOfToday, startOfTomorrow } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { Button, InputGroup } from 'react-bootstrap';
import { Calendar2XFill, Download, Search } from 'react-bootstrap-icons';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { pathnames } from '../../api';

import { parseUrlQuery, stringifyUrlQuery } from '../../api/utils';
import MeasurementDeviceItem from '../../models/data/MeasurementDeviceItem';
import ItemIdentifier from '../../models/requests/ItemIdentifier';
import {
  parseBegin,
  parseEnd,
  parseItem,
  toSearchValuesRequestParams,
} from '../../models/requests/SearchValuesRequest';
import DataTable from '../data-table';
import DateTimePicker from '../date-time-picker';
import SelectTabs from '../select-tabs';

const MinDate = new Date(2020, 0, 1);

const DataList: React.FunctionComponent = () => {
  // ---------------------------------------------------------------------
  // Get URL Query Parameters
  const history = useHistory();
  const urlZip = history.createHref({ pathname: pathnames.zipValues });
  // ---------------------------------------------------------------------
  // Status
  const [itemIdentifiers, setItemIdentifiers] = useState<ItemIdentifier[]>([]); // for URL Query Parameters
  const [begin, setBegin] = useState(startOfToday());
  const [end, setEnd] = useState(startOfTomorrow());
  const [items, setItems] = useState<MeasurementDeviceItem[]>([]);
  const [search, setSearch] = useState<string | undefined>();
  const [table, setTable] = useState<{
    items: MeasurementDeviceItem[];
    begin: Date;
    end: Date;
  }>({
    begin,
    end,
    items: [],
  });
  // ---------------------------------------------------------------------
  // Callbacks & Functions

  // ---------------------------------------------------------------------
  // Effects
  //  --- Initialize
  useEffect(() => {
    let unmounted = false;
    const query = parseUrlQuery(history.location.search);
    if (query.begin != null) {
      const parsedBegin = parseBegin(query.begin);
      if (parsedBegin == null) {
        toast.error(() => (
          <>
            <Calendar2XFill />
            <span>{' 開始日時 begin が不正です'}</span>
            <small>{` (URL)`}</small>
          </>
        ));
      } else if (!unmounted) {
        setBegin(parsedBegin);
      }
    }
    if (query.end != null) {
      const parsedEnd = parseEnd(query.end);
      if (parsedEnd == null) {
        toast.error(() => (
          <>
            <Calendar2XFill />
            <span>{' 終了日時 end が不正です'}</span>
            <small>{` (URL)`}</small>
          </>
        ));
      } else if (!unmounted) {
        setEnd(parsedEnd);
      }
    }
    if (query.item != null) {
      if (!unmounted) {
        setItemIdentifiers(parseItem(query.item));
      }
    }
    return () => {
      unmounted = true;
    };
  }, []);
  useEffect(() => {
    if (begin <= end && items.length > 0) {
      setSearch(
        `?${stringifyUrlQuery(
          toSearchValuesRequestParams({
            begin,
            end,
            item: items,
          }),
        )}`,
      );
    } else {
      setSearch(undefined);
    }
  }, [begin, end, items]);
  // ---------------------------------------------------------------------
  // Components
  return (
    <div>
      <SelectTabs
        defaultItemIdentifiers={itemIdentifiers}
        onChange={(newItems) => {
          setItems(newItems);
        }}
      />
      <div className="d-flex justify-content-between px-3 py-2">
        <InputGroup>
          <InputGroup.Prepend>
            <InputGroup.Text>計測日時</InputGroup.Text>
          </InputGroup.Prepend>
          <DateTimePicker
            variant="outline-secondary"
            minDate={MinDate}
            // startDate={begin}
            // endDate={end}
            // selectsStart
            selected={begin}
            onChange={(date) => {
              if (date instanceof Date) {
                setBegin(date);
              }
            }}
          />
          <InputGroup.Text>～</InputGroup.Text>
          <DateTimePicker
            variant="outline-secondary"
            minDate={MinDate}
            // startDate={begin}
            // endDate={end}
            // selectsEnd
            selected={end}
            onChange={(date) => {
              if (date instanceof Date) {
                setEnd(date);
              }
            }}
          />
          <InputGroup.Append>
            <Button
              variant="primary"
              disabled={search == null}
              onClick={() => {
                // URL変更
                history.push({
                  pathname: history.location.pathname,
                  search,
                });
                // テーブル表示変更
                setTable({
                  begin,
                  end,
                  items,
                });
              }}
            >
              <span className="pr-2">計測装置データ検索</span>
              <Search size="1.25rem" />
            </Button>
          </InputGroup.Append>
        </InputGroup>
        <a
          href={search == null ? undefined : [urlZip, search].join('')}
          className={`btn btn-danger align-middle ${
            search == null ? 'disabled' : ''
          }`}
          tabIndex={search == null ? -1 : undefined}
          role="button"
          aria-disabled={search == null ? true : undefined}
          target="_blank"
          rel="noreferrer"
          download
          title="ダウンロード"
        >
          <Download size="1.25rem" />
        </a>
      </div>
      <DataTable items={table.items} begin={table.begin} end={table.end} />
    </div>
  );
};

export default DataList;
